// src/components/layout/NavigationSidebar.tsx
//
// Left sidebar — primary nav, design system v1.
//
// Visual model:
//   - Surface: --color-bg-subtle (light grey in light mode, near-black in dark)
//   - Items: muted text, hover → muted background
//   - Active item: accent left-border (3px) + accent-subtle background + accent text
//   - Per design system §6.5 — accent left-border on the active route is the
//     primary nav signal, not coloured backgrounds or filled blocks.

import { Link, useLocation } from '@tanstack/react-router';
import clsx from 'clsx';
import {
  Squares2X2Icon,
  CubeIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  UsersIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/solid';
import { usePermissions } from '../../hooks/usePermissions';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
}

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: Squares2X2Icon },
  { path: '/assets/', label: 'Assets', icon: CubeIcon },
  { path: '/non-infosec-risk/', label: 'Business Risk', icon: ClipboardDocumentListIcon },
  { path: '/reports/', label: 'Reports', icon: ChartBarIcon },
  { path: '/risk/', label: 'InfoSec Risk', icon: ExclamationTriangleIcon },
  { path: '/soa/', label: 'SoA', icon: ShieldCheckIcon },
  { path: '/raci/', label: 'RACI', icon: UserGroupIcon },
  { path: '/admin/', label: 'Admin', icon: Cog6ToothIcon, requireAdmin: true },
  { path: '/user-management/', label: 'Users', icon: UsersIcon, requireAdmin: true },
  { path: '/platform-admin/', label: 'Platform', icon: BuildingOffice2Icon, requireSuperAdmin: true },
  { path: '/guide/', label: 'User guide', icon: QuestionMarkCircleIcon },
];

interface NavigationSidebarProps {
  className?: string;
}

export function NavigationSidebar({ className }: NavigationSidebarProps) {
  const location = useLocation();
  const currentPath = location.pathname;
  const { canAccessAdmin, isSuperAdmin } = usePermissions();

  const visibleItems = navItems.filter((item) => {
    if (item.requireSuperAdmin) return isSuperAdmin;
    if (item.requireAdmin) return canAccessAdmin;
    return true;
  });

  return (
    <div
      className={clsx('flex flex-col w-60 border-r', className)}
      style={{
        background: 'var(--color-bg-subtle)',
        borderColor: 'var(--color-border)',
      }}
    >
      {/* Sidebar header — small label, no oversize block */}
      <div
        className="px-4 py-4 border-b text-xs font-semibold uppercase tracking-wider"
        style={{
          borderColor: 'var(--color-border)',
          color: 'var(--color-text-subtle)',
        }}
      >
        Workspace
      </div>

      {/* Navigation items */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          // Normalise trailing slashes — TanStack's pathname comes back without
          // a trailing slash for `/assets`, but our nav items declare `/assets/`
          // to match the file-route registration. Strip both sides before
          // comparing so the active highlight works on every page, not just `/`.
          const stripSlash = (p: string) =>
            p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
          const cur = stripSlash(currentPath);
          const target = stripSlash(item.path);
          const isActive =
            cur === target || (target !== '/' && cur.startsWith(target + '/'));

          return (
            <Link
              key={item.path}
              to={item.path}
              className={clsx(
                'group relative flex items-center gap-3 px-3 py-2 rounded-lg font-medium text-sm transition-colors',
              )}
              style={{
                color: isActive ? 'var(--color-accent)' : 'var(--color-text-muted)',
                background: isActive ? 'var(--color-accent-subtle)' : 'transparent',
              }}
            >
              {/* Accent left-border on active — per design system §6.5 */}
              {isActive && (
                <span
                  className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r"
                  style={{ background: 'var(--color-accent)' }}
                />
              )}
              <Icon
                className="w-5 h-5 flex-shrink-0"
                style={{
                  color: isActive ? 'var(--color-accent)' : 'var(--color-text-subtle)',
                }}
              />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Suite branding footer */}
      <div
        className="px-4 py-3 border-t text-center"
        style={{
          borderColor: 'var(--color-border)',
        }}
      >
        <p className="text-xs" style={{ color: 'var(--color-text-subtle)' }}>
          Created using Certain
          <span
            className="font-medium"
            style={{ fontSize: '0.7em', verticalAlign: 'super', lineHeight: 0 }}
          >
            ®
          </span>
        </p>
        <p className="text-xs" style={{ color: 'var(--color-text-subtle)' }}>
          Asset Risk Manager
        </p>
      </div>
    </div>
  );
}
